import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tfaCode, setTfaCode] = useState('')
  const [showTFA, setShowTFA] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { login, verifyTFA } = useAuth()

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const result = await login(email, password)
      if (result.requires_tfa) {
        setShowTFA(true)
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  async function handleTFA(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await verifyTFA(tfaCode)
    } catch (err: any) {
      // Handle both string errors and Pydantic validation error arrays
      const detail = err.response?.data?.detail
      if (typeof detail === 'string') {
        setError(detail)
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        setError(detail[0].msg)
      } else {
        setError('Invalid code. Try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4 flex items-center justify-center min-h-screen">
        <div className="card max-w-md w-full">
          {!showTFA ? (
            <>
              <h1 className="text-2xl font-bold mb-2">Welcome back</h1>
              <p className="text-dark-400 mb-6">Sign in to access your reports and dashboard.</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    required
                  />
                </div>

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button type="submit" disabled={loading} className="btn btn-primary w-full">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <p className="text-center text-dark-400 text-sm mt-6">
                Don't have an account?{' '}
                <Link to="/register" className="text-ginger-500 hover:underline">
                  Create one
                </Link>
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2">Two-Factor Authentication</h1>
              <p className="text-dark-400 mb-6">
                Enter the 6-digit code from your authenticator app.
              </p>

              <form onSubmit={handleTFA} className="space-y-4">
                <div>
                  <label className="label">Authentication Code</label>
                  <input
                    type="text"
                    value={tfaCode}
                    onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="input text-center text-2xl tracking-widest"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button type="submit" disabled={loading || tfaCode.length !== 6} className="btn btn-primary w-full">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </form>

              <button
                onClick={() => setShowTFA(false)}
                className="text-dark-400 text-sm mt-4 hover:text-dark-100 block mx-auto"
              >
                ← Back to login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
